import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getProfile, login as loginApi, LoginParams, UserInfo } from '../api/auth';

interface AuthContextType {
  user: UserInfo | null;
  loading: boolean;
  login: (params: LoginParams) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const userInfo = await getProfile();
      if (userInfo.role !== 'admin' && userInfo.role !== 'super_admin') {
        localStorage.removeItem('admin_token');
        setUser(null);
      } else {
        setUser(userInfo);
      }
    } catch {
      localStorage.removeItem('admin_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (params: LoginParams) => {
    const { token, user } = await loginApi(params);
    localStorage.setItem('admin_token', token);
    if (user.role !== 'admin' && user.role !== 'super_admin') {
      localStorage.removeItem('admin_token');
      throw new Error('无管理员权限');
    }
    setUser(user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
